import { Component, OnInit } from '@angular/core';
import { AdvisoryFee, CustomAdvisoryFee } from '@ddt-web/advisory-fee';
import { ApplicationPathConstants } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { distinctUntilChangedDeep, InjectQueryParam, Memoize } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { MeQueryParamKey, UrlService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as isUndefined from 'lodash/isUndefined';
import { forkJoin, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { ModelsBrowseDataService } from '../services/models-browse-data.service';

@UntilDestroy()
@Component({
	selector: 'mb-family-performance',
	templateUrl: './family-performance.component.html',
	styleUrls: ['./family-performance.component.scss']
})
export class FamilyPerformanceComponent implements OnInit {
	@InjectQueryParam(MeQueryParamKey.GROUP_ID) public readonly groupId: string;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_PERFORMANCE_PAGE_BOTTOM_DISCLOSURE) public readonly enablePerformancePageBottomDisclosure: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_PERFORMANCE_BOX_DISCLOSURE) public readonly enablePerformanceBoxDisclosure: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.PERFORMANCE_BOX_DISCLOSURE_ENABLED_FOR_BLK_GROUP_FAMILY)
	private readonly performanceBoxDisclosureEnabledBlkFamilies: Array<string> = [];
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_FAMILY_SPECIFIC_PERFORMANCE_BENCHMARK_DISCLOSURE)
	private readonly enableFamilySpecificDisclosure: boolean = true;
	@InjectConfig(SharedConfigKey.CUSTOM_ADVISORY_FEE) private readonly customAdvisoryFee: AdvisoryFee = CustomAdvisoryFee;
	@InjectConfig(SharedConfigKey.EnableCustomFee) private readonly isCustomFeeEnabled: boolean = true;
	public familyIds: Observable<Array<string>>;
	public familyId: string;
	public appPaths = ApplicationPathConstants.MODELS_BROWSE_PATHS;
	// To check if the performance-API is returning any error, will keep trying to load the template unless turned false
	public isPerformanceResponseValid: boolean = true;
	public customAdvisoryFee$: Observable<number>;
	public familyIdentificationForDcr: Observable<string>;
	public modelBenchmarkDisclaimer$: Observable<string | undefined>;
	public familyAladdinTicker$: Observable<string>;
	public readonly blkFamilyGroup = SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP;
	constructor(
		private urlService: UrlService,
		private modelsBrowseDataService: ModelsBrowseDataService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService
	) { }

	public ngOnInit(): void {
		this.familyId = this.urlService.getModelFamilyId();

		if (!this.familyId) {
			// handled on parent level
			return;
		}

		this.familyAladdinTicker$ = this.modelsBrowseDataService.getAladdinTicker(this.familyId);
		this.modelBenchmarkDisclaimer$ = this.modelsBrowseDataService.getBenchmarkDisclaimer(this.familyId);
		if (this.enableFamilySpecificDisclosure) {
			this.familyIdentificationForDcr = this.familyAladdinTicker$.pipe(map(ticker => ticker + '_DISCLOSURE'));
		}

		if (this.isCustomFeeEnabled) {
			this.customAdvisoryFee$ = this.applicationStateStoreProxyService.getPerformanceState().pipe(
				untilDestroyed(this),
				map((state: PerformanceState) => state.customAdvisoryFee)
			);

			this.customAdvisoryFee$
				.pipe(
					distinctUntilChangedDeep(),
					filter(customFee => !isUndefined(customFee)),
					switchMap(() =>
						forkJoin([this.modelsBrowseDataService.fetchFamilyModelsAnalytics(this.familyId), this.modelsBrowseDataService.fetchFamilyHoldings(this.familyId)])
					),
					untilDestroyed(this)
				)
				.subscribe(([modelsAvailable, modelHoldings]) => {
					// Will show error template if turned false
					if (!modelsAvailable && !modelHoldings) {
						this.isPerformanceResponseValid = false;
					}
					this.familyIds = of([this.familyId]);
				});
		} else {
			forkJoin([this.modelsBrowseDataService.fetchFamilyModelsAnalytics(this.familyId), this.modelsBrowseDataService.fetchFamilyHoldings(this.familyId)])
				.pipe(untilDestroyed(this))
				.subscribe(([modelsAvailable, modelHoldings]) => {
					// Will show error template if turned false
					if (!modelsAvailable && !modelHoldings) {
						this.isPerformanceResponseValid = false;
					}
					this.familyIds = of([this.familyId]);
				});
		}
	}

	@Memoize()
	public isNetAndGrossDateEqual(data: ComponentData): boolean {
		const performanceTabData = data.getTabById('performance');
		const performanceCustomNetTabData = data.getTabById('performance.custom.net');

		const asOfDateGross = (performanceTabData?.tableData?.[0] as any)?.rows?.[0]?.asOfDate?.formattedValue ?? '';
		const asOfDateNet = (performanceCustomNetTabData?.tableData?.[0] as any)?.rows?.[0]?.asOfDate?.formattedValue ?? '';

		return !asOfDateGross || !asOfDateNet || asOfDateGross === asOfDateNet;
	}
}
